import { Box } from '@mui/system'
import { Typography } from '@mui/material'
import { DateTime, Duration } from 'luxon'
import { type FC, Fragment, useCallback, useMemo, useState } from 'react'
import { Camera, type OutdoorEngagement } from '../../../../../api/models'
import { CameraTimeline } from './CameraTimeline'
import { CameraTimelineHeader } from './CameraTimelineHeader'
import type { EngagementRecordings, RecordingViewedDetails } from '../types'
import type { EngagementRecordingVideoElement } from '../InspectPlayer'
import { getCameraEntriesInDisplayOrder } from '../utils/recordingsUtils'
import { useResizeObserver } from '../../../../../hooks/useResizeObserver'
import { UNKNOWN_TYPE } from '../../../../monitoring-moderation/CameraOverviewPage/cameraOverviewEnums'
import { getDisplayName } from '../../../../../utils/cameraUtils'

interface InspectTimelinesProps {
  engagementRecordings: EngagementRecordings
  cameras: Camera[]
  engagement: OutdoorEngagement
  selectedCameraId: string | null
  onCameraTimelineClick: (cameraId: string, absoluteTimeMs: number) => void
  // Selects a camera without touching its playhead - the label switches which camera is shown, whereas
  // clicking the track (onCameraTimelineClick) also seeks it.
  onCameraSelect: (cameraId: string) => void
  viewedDetailsByRecordingId: Map<string, RecordingViewedDetails[]>
  // Per-camera currently-playing video element (keyed by cameraId), for each timeline's live scrubber.
  videoElements: Map<string, EngagementRecordingVideoElement>
}

// Minimum horizontal space to reserve per tick label (px). The tick count is derived from the ruler's
// measured width / this value, so labels get sparser on narrow screens and denser on wide ones rather
// than overlapping.
const MIN_TICK_SPACING_PX = 64

// Nice round intervals (ms) to snap to when choosing tick spacing.
const TICK_INTERVALS_MS = [
  1_000, // 1 s
  2_000, // 2 s
  5_000, // 5 s
  10_000, // 10 s
  15_000, // 15 s
  30_000, // 30 s
  60_000, // 1 min
  120_000, // 2 min
  300_000 // 5 min
]

// Finest round interval whose resulting tick count fits within maxTicks, so ticks never crowd past the
// available width. TICK_INTERVALS_MS is ascending, so the first that fits is the densest that still does.
function getTickIntervalMs(totalMs: number, maxTicks: number): number {
  for (const interval of TICK_INTERVALS_MS) {
    if (Math.floor(totalMs / interval) + 1 <= maxTicks) return interval
  }
  return TICK_INTERVALS_MS[TICK_INTERVALS_MS.length - 1]
}

export const InspectTimelines: FC<InspectTimelinesProps> = ({
  engagementRecordings,
  cameras,
  engagement,
  selectedCameraId,
  onCameraTimelineClick,
  onCameraSelect,
  viewedDetailsByRecordingId,
  videoElements
}) => {
  // The ruler's measured pixel width drives how many ticks we draw (see MIN_TICK_SPACING_PX), so labels
  // thin out on narrow screens and fill in on wide ones instead of overlapping - recomputed on resize.
  const [rulerEl, setRulerEl] = useState<HTMLDivElement | null>(null)
  const [rulerWidth, setRulerWidth] = useState(0)

  /**
   * array of cameras with recordings, with the triggering always placed camera first
   */
  const cameraEntries = useMemo(
    () => getCameraEntriesInDisplayOrder(engagementRecordings.recordingsByCameraId, engagement.triggeringDevice.id, cameras),
    [engagementRecordings, engagement.triggeringDevice.id, cameras]
  )

  // this is the earlist recording start time across all cameras
  const earliestMs = useMemo(
    () => (engagementRecordings.earliestStartTime ? DateTime.fromISO(engagementRecordings.earliestStartTime).toMillis() : null),
    [engagementRecordings.earliestStartTime]
  )

  // this is the latest recording end time across all cameras
  const latestMs = useMemo(
    () => (engagementRecordings.latestEndTime ? DateTime.fromISO(engagementRecordings.latestEndTime).toMillis() : null),
    [engagementRecordings.latestEndTime]
  )

  // the total duration of all recordings across all cameras
  const totalDurationMs = earliestMs !== null && latestMs !== null ? latestMs - earliestMs : null

  // this is the engagement start time which is different from the earliest recording start time
  const engagementStartMs = useMemo(
    () => (engagement.createdTimestamp ? DateTime.fromISO(engagement.createdTimestamp).toMillis() : null),
    [engagement.createdTimestamp]
  )

  // Where the engagement started, as a % across the shared timeline axis (0 = left edge, 100 = right).
  // Footage can begin before the engagement did, so this marks "the engagement starts here": it positions
  // the vertical engagement-start boundary line below, and each CameraTimeline uses it to start the gray
  // band of its track shading. Null when it would sit exactly on the left edge (or times are missing),
  // in which case no line is drawn.
  const relativeStartTimePercent =
    totalDurationMs && earliestMs !== null && engagementStartMs !== null && engagementStartMs !== earliestMs
      ? Math.max(0, Math.min(100, ((engagementStartMs - earliestMs) / totalDurationMs) * 100))
      : null

  const ticks = useMemo<Array<{ percent: number; label: string; end?: boolean }>>(() => {
    if (!totalDurationMs || earliestMs === null || latestMs === null) return []
    const maxTicks = Math.max(2, Math.floor(rulerWidth / MIN_TICK_SPACING_PX))
    const interval = getTickIntervalMs(totalDurationMs, maxTicks)
    const result: Array<{ percent: number; label: string; end?: boolean }> = []
    // Stop before the end (strict <) so the explicit end tick below is never duplicated.
    for (let offset = 0; offset < totalDurationMs; offset += interval) {
      result.push({
        percent: (offset / totalDurationMs) * 100,
        label: Duration.fromMillis(offset).toFormat('hh:mm:ss')
      })
    }
    // Always anchor a tick to the right edge (the end time); drop the previous tick if it would collide.
    const minGapPercent = rulerWidth > 0 ? (MIN_TICK_SPACING_PX / rulerWidth) * 100 : 0
    const lastRegular = result[result.length - 1]
    if (lastRegular && 100 - lastRegular.percent < minGapPercent) result.pop()
    result.push({ percent: 100, label: Duration.fromMillis(totalDurationMs).toFormat('hh:mm:ss'), end: true })
    return result
  }, [totalDurationMs, earliestMs, latestMs, rulerWidth])

  // Row indices (1-based) for each section of the grid.
  // All grid items use explicit placement so that the engagement-boundary overlay
  // (which spans all rows in column 2) does not trigger the auto-placement algorithm
  // and accidentally push camera items out of column 2.
  const spacerRow = cameraEntries.length + 1
  const rulerRow = cameraEntries.length + 2

  // getDisplayName will log missing camera model errors to faro, so we memoize the results
  // to avoid spamming faro during re-renders.
  const displayNameByCameraId = useMemo(() => new Map(cameras.map(c => [c.uuid, getDisplayName(c.model) ?? UNKNOWN_TYPE])), [cameras])

  const handleRulerResize = useCallback((el: HTMLDivElement) => setRulerWidth(el.clientWidth), [])

  useResizeObserver(rulerEl, handleRulerResize, { debounceInterval: 100 })

  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: 'fit-content(30%) 1fr',
        gridTemplateRows: `repeat(${cameraEntries.length}, auto) 1fr auto`,
        columnGap: 'var(--spacing-m)',
        rowGap: 'var(--spacing-m)',
        alignItems: 'start'
      }}
    >
      {cameraEntries.map(([cameraId, cameraRecordings], index) => {
        const camera = cameras.find(c => c.uuid === cameraId)
        const cameraType = displayNameByCameraId.get(cameraId) ?? UNKNOWN_TYPE

        const startTime = cameraRecordings.reduce<string>((min, clip) => (!min || clip.startTime < min ? clip.startTime : min), '')

        // This camera's viewed details, gathered across its clips (empty until inspect mode has events).
        const cameraViewedDetails = cameraRecordings.flatMap(rec => viewedDetailsByRecordingId.get(rec.recordingId) ?? [])

        const isSelected = selectedCameraId === cameraId
        const row = index + 1
        const accessAllowed = !engagementRecordings.deniedReasonByCameraId.has(cameraId)

        return (
          <Fragment key={cameraId}>
            <CameraTimelineHeader
              cameraId={cameraId}
              cameraName={camera?.name ?? ''}
              cameraType={cameraType}
              rowIndex={row}
              isSelected={isSelected}
              accessAllowed={accessAllowed}
              hasRecordings={cameraRecordings.length > 0}
              onCameraSelect={onCameraSelect}
            />
            <Box gridRow={row} gridColumn={2}>
              <CameraTimeline
                cameraId={cameraId}
                startTime={startTime}
                isActive={true}
                activeVideoElement={videoElements.get(cameraId) ?? null}
                recordings={engagementRecordings}
                engagement={engagement}
                isSelected={isSelected}
                relativeStartTimePercent={relativeStartTimePercent}
                cameraViewedDetails={cameraViewedDetails}
                onClick={absoluteTimeMs => onCameraTimelineClick(cameraId, absoluteTimeMs)}
              />
            </Box>
          </Fragment>
        )
      })}

      <Box sx={{ gridRow: spacerRow, gridColumn: '1 / -1' }} />

      {/* Engagement start boundary line — spans column 2 up to (not including) the ruler row.
          Explicit placement on every grid item above is required so this overlay doesn't
          trigger auto-placement and push the camera timelines out of column 2.
          There is deliberately no matching end line: the axis is capped at the engagement end
          (capRecordingsAtEngagementEnd), so an end line could only ever sit on the right border. */}
      {relativeStartTimePercent !== null && (
        <Box
          sx={{
            gridRow: `1 / ${rulerRow}`,
            gridColumn: 2,
            position: 'relative',
            alignSelf: 'stretch',
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          <Box
            data-testid="engagement-start-line"
            sx={{
              position: 'absolute',
              left: `${relativeStartTimePercent}%`,
              top: 0,
              bottom: 'calc(-1 * var(--spacing-m))',
              width: '1px',
              background: 'var(--border-medium)'
            }}
          />
        </Box>
      )}

      {/* Time ruler — pinned to the bottom of the grid */}
      {ticks.length > 0 && (
        <>
          <Box sx={{ gridRow: rulerRow, gridColumn: 1 }} /> {/* spacer for the label column */}
          <Box
            ref={(el: HTMLDivElement | null) => setRulerEl(el)}
            sx={{ gridRow: rulerRow, gridColumn: 2, position: 'relative', height: '2.25rem', alignSelf: 'start', overflow: 'visible' }}
          >
            {/* Single horizontal line connecting all tick marks */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'var(--border-medium)'
              }}
            />
            {ticks.map(({ percent, label, end }) => (
              <Fragment key={label}>
                {/* Notch — positioned directly at the tick percentage, no intermediate container */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${percent}%`,
                    top: 0,
                    width: '1px',
                    height: '1rem',
                    background: 'var(--border-medium)'
                  }}
                />
                {/* Label — centered under the notch independently */}
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: `${percent}%`,
                    top: `calc(1rem + var(--spacing-3xs))`,
                    transform: end ? 'translateX(-100%)' : 'translateX(-50%)',
                    fontSize: 'var(--size-3xs)',
                    lineHeight: 1.2,
                    color: 'var(--text-secondary)',
                    userSelect: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {label}
                </Typography>
              </Fragment>
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}

InspectTimelines.displayName = 'InspectTimelines'
